import { Badge, Button, Callout, Dialog, Flex, Text } from "@radix-ui/themes";
import { isSourceBusy } from "hooks/aiAssistant/useKnowledge";

export const formatType = (value) => value ? value.replaceAll("_", " ") : "—";
export const formatTime = (value) => value ? new Date(value).toLocaleString() : "—";
export const formatInterval = (minutes) => minutes % 10080 === 0 ? `${minutes / 10080} ${minutes === 10080 ? "week" : "weeks"}`
  : minutes % 1440 === 0 ? `${minutes / 1440} ${minutes === 1440 ? "day" : "days"}`
    : minutes % 60 === 0 ? `${minutes / 60} ${minutes === 60 ? "hour" : "hours"}` : `${minutes} min`;

export function knowledgeErrorMessage(error, action) {
  const status = error?.response?.status;
  if (status === 409) return action === "sync" ? "A synchronization is already in progress."
    : "This source could not be saved. Its URL may already exist or synchronization may be in progress. Reload and retry.";
  if (status === 400 || status === 422) return "Check the submitted fields, adapter, URL and synchronization interval.";
  if (status === 401) return "Your session has expired. Sign in again.";
  if (status === 403) return "Only organisation Owners can manage Knowledge.";
  if (status === 404) return "The requested Knowledge resource was not found. Check Assistant Settings or reload.";
  if (status === 504) return "Knowledge service timed out. Reload to check its state before retrying.";
  return "Knowledge service is temporarily unavailable.";
}

export function KnowledgeLoadError({ error, onRetry }) {
  return <Callout.Root color="red" role="alert"><Callout.Text>{knowledgeErrorMessage(error)}</Callout.Text>
    <Button variant="soft" color="gray" onClick={onRetry}>Retry</Button></Callout.Root>;
}

const ERROR_COPY = {
  source_timeout: "The source request timed out.", source_http: "The source could not be fetched.",
  source_json: "The source did not return the expected JSON data.", source_size: "The source exceeded the size limit.",
  invalid_snapshot: "The source data did not pass validation.", invalid_configuration: "Check the source configuration.",
  source_disabled: "The source was disabled.", publish_failed: "Synchronization could not be queued. Try again.",
  embedding_authentication: "AI indexing credentials are unavailable. Contact support.",
  embedding_billing: "AI indexing is unavailable. Contact support.",
  embedding_model_unavailable: "AI indexing is unavailable. Contact support.",
  embedding_timeout: "AI indexing timed out.", database_unavailable: "Knowledge storage is temporarily unavailable.",
};
export function SafeSyncError({ error }) {
  return error ? <details className="knowledge-sync-error"><summary>Synchronization error</summary>
    <Text size="2">{ERROR_COPY[error] || "Synchronization failed. Retry or contact support if the problem continues."}</Text></details> : null;
}

export function sourceHealth(source) {
  const activity = source.activity_status;
  if (activity === "reservation_expired") return ["Reservation expired", "amber"];
  if (activity === "queued") return ["Queued", "blue"];
  if (activity === "retry_wait") return ["Waiting to retry", "amber"];
  if (activity === "running" || source.sync_status === "running") return ["Running", "blue"];
  if (isSourceBusy(source)) return ["Reserved", "amber"];
  if (!source.enabled) return ["Disabled", "gray"];
  if (source.sync_status === "failed" || ["failed", "abandoned"].includes(source.latest_sync_run?.pipeline_status)) return ["Failed", "red"];
  if (!source.sync_enabled) return ["Auto-sync off", "gray"];
  if (source.sync_status === "succeeded") return ["Healthy", "green"];
  return ["Awaiting first sync", "gray"];
}

export function SourceHealth({ source }) {
  const [label, color] = sourceHealth(source);
  return <Badge color={color}>{label}</Badge>;
}

export function KnowledgeDialog({ title, description, onClose, children, busy = false, wide = false }) {
  return <Dialog.Root open onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <Dialog.Content maxWidth={wide ? "1000px" : "720px"} className="knowledge-dialog" aria-busy={busy}>
      <Flex justify="between" align="start" gap="4"><Dialog.Title>{title}</Dialog.Title>
        <Button variant="ghost" color="gray" aria-label="Close dialog" disabled={busy} onClick={onClose}>Close</Button></Flex>
      <Dialog.Description size="2" mb="4">{description}</Dialog.Description>
      {children}
    </Dialog.Content>
  </Dialog.Root>;
}

export function Pagination({ data, onPage, busy = false }) {
  if (!data) return null;
  const pages = Math.max(1, Math.ceil(data.total / data.limit));
  return <Flex justify="between" align="center" gap="3" wrap="wrap" className="knowledge-pagination">
    <Text size="2" color="gray">{data.total.toLocaleString()} results · Page {data.page} of {pages}</Text>
    <Flex gap="2"><Button variant="soft" color="gray" disabled={busy || data.page <= 1} onClick={() => onPage(data.page - 1)}>Previous</Button>
      <Button variant="soft" color="gray" disabled={busy || data.page >= pages || data.page >= 10000} onClick={() => onPage(data.page + 1)}>Next</Button></Flex>
  </Flex>;
}

export function PollingNotice({ query }) {
  return query.pollingPaused ? <Callout.Root color="amber"><Callout.Text>
    Synchronization is still active. Automatic refresh has paused after two minutes; use Refresh to check again.
  </Callout.Text></Callout.Root> : null;
}

export function SourceTimes({ source }) {
  return <dl className="knowledge-facts">
    <div><dt>Last successful sync</dt><dd>{formatTime(source.last_successful_at)}</dd></div>
    <div><dt>Next sync</dt><dd>{formatTime(source.next_sync_at)}</dd></div>
  </dl>;
}
